import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { SidebarComponent, NavItem } from '../../layout/main-layout/sidebar/sidebar.component';
import { TopbarComponent } from '../../layout/main-layout/topbar/topbar.component';
import { DialogModule } from 'primeng/dialog';
import { AuthService } from '../../auth/auth.service';
import { AclService } from '../../acl/acl.service';
import { DashboardStateService } from '../../../services/dashboard-state.service';
import { SidebarStateService } from '../../../services/sidebar-state.service';
import { AppModule, ModuleService } from '../../../services/apis/module/module.service';
import { IntegrationControlService } from '../../../services/apis/integration-control/integration-control.service';
import { EMPTY, Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-content',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SidebarComponent, TopbarComponent, DialogModule],
  templateUrl: './content.component.html',
})
export class ContentComponent implements OnInit, OnDestroy {
  readonly sidebar = inject(SidebarStateService);
  readonly auth = inject(AuthService);
  private readonly acl = inject(AclService);
  readonly dashboardState = inject(DashboardStateService);
  private readonly router = inject(Router);
  private readonly moduleService = inject(ModuleService);
  private readonly integrationControlService = inject(IntegrationControlService);
  private readonly sub = new Subscription();

  showXeroReconnectDialog = false;

  navItems: NavItem[] = [];
  showDashboardControls = this.shouldShowDashboardControls(this.router.url);
  showTopbar = this.shouldShowTopbar(this.router.url);
  private readonly hiddenSidebarModuleKeys = new Set(['sales', 'purchases', 'reports']);
  private readonly hiddenSidebarRoutes = new Set(['/sales', '/purchases', '/reports', '/comparison-report']);
  private readonly revenueNavItem: NavItem = { label: 'Revenue', icon: 'pi-chart-line', route: '/revenue' };
  private readonly revenueAdjacentNavItems: NavItem[] = [
    { label: 'Cash and Credit Control', icon: 'pi-credit-card', route: '/cash-and-credit-control' },
    { label: 'Balance Sheet items', icon: 'pi-list', route: '/balance-sheet-item-name' },
    { label: 'Team performance', icon: 'pi-users', route: '/team-performance' },
    { label: 'Clients', icon: 'pi-briefcase', route: '/clients' },
    { label: 'Placements', icon: 'pi-id-card', route: '/placements' },
    { label: 'Timesheets', icon: 'timesheet-document-clock', route: '/timesheets' },
  ];

  // Fallback used when the modules table hasn't been seeded yet
  private readonly fallbackNavItems: NavItem[] = [
    { label: 'Overview', icon: 'pi-chart-bar', route: '/dashboard' },
    { label: 'Revenue', icon: 'pi-chart-line', route: '/revenue' },
    { label: 'Cash and Credit Control', icon: 'pi-credit-card', route: '/cash-and-credit-control' },
    { label: 'Balance Sheet items', icon: 'pi-list', route: '/balance-sheet-item-name' },
    { label: 'Team performance', icon: 'pi-users', route: '/team-performance' },
    { label: 'Clients', icon: 'pi-briefcase', route: '/clients' },
    { label: 'Placements', icon: 'pi-id-card', route: '/placements' },
    { label: 'Timesheets', icon: 'timesheet-document-clock', route: '/timesheets' },
    // { label: 'Sales',     icon: 'pi-briefcase',     route: '/sales' },
    // { label: 'Purchases', icon: 'pi-shopping-cart', route: '/purchases' },
    // { label: 'Reports',   icon: 'pi-file-export',   route: '/reports' },
    { label: 'Settings', icon: 'pi-cog', route: '/settings' },
  ];

  ngOnInit(): void {
    this.sub.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.showDashboardControls = this.shouldShowDashboardControls(this.router.url);
        this.showTopbar = this.shouldShowTopbar(this.router.url);
        if (this.sidebar.isMobile()) {
          this.sidebar.close();
        }
      })
    );

    this.loadNavigation();
    this.sub.add(this.moduleService.menuChanged$.subscribe(() => this.loadNavigation()));
    this.startXeroConnectionPolling();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  openXeroIntegrationSettings(): void {
    this.showXeroReconnectDialog = false;
    void this.router.navigate(['/settings/integrations']);
  }

  private startXeroConnectionPolling(): void {
    this.sub.add(
      timer(0, 60_000).pipe(
        switchMap(() => this.integrationControlService.getXeroConnections().pipe(
          catchError(() => EMPTY)
        ))
      ).subscribe(connections => {
        this.showXeroReconnectDialog = connections.some(connection =>
          connection.reconnectRequired === true
          || (connection.connectionStatus ?? '').toLowerCase() === 'reconnectrequired'
        );
      })
    );
  }

  private shouldShowDashboardControls(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return !path.startsWith('/placements') &&
      !path.startsWith('/timesheets') &&
      !path.startsWith('/settings');
  }

  private shouldShowTopbar(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return !path.startsWith('/settings');
  }

  /**
   * All modules are open to any authenticated user except Settings, which is a hard
   * role gate (SuperAdmin or the "Admin" role) — it isn't a granular-permission module.
   */
  private getPermissionFilteredFallback(): NavItem[] {
    return this.fallbackNavItems
      .filter(item => !this.hiddenSidebarRoutes.has(item.route))
      .filter(item => item.route !== '/settings' || this.acl.canAccessSettings());
  }

  private loadNavigation(): void {
    // The tenant configuration controls which modules the organisation has enabled.
    // /menu/me remains a compatibility fallback when the tenant endpoint is not
    // available to the current account. Settings is still ACL-filtered below.
    const menuRequest = this.moduleService.getTenantMenuConfiguration().pipe(
      switchMap(modules => modules.length > 0
        ? of(modules)
        : this.moduleService.getMyMenu()),
      catchError(() => this.moduleService.getMyMenu()),
    );

    this.sub.add(menuRequest.subscribe({
      next: modules => {
        this.navItems = modules
          .filter(module => module.isActive)
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(module => this.toNavItem(module))
          .filter(item => !this.hiddenSidebarRoutes.has(item.route))
          .filter(item => item.route !== '/settings' || this.acl.canAccessSettings());
      },
      error: () => {
        this.navItems = this.getPermissionFilteredFallback();
      },
    }));
  }

  private toNavItem(module: AppModule): NavItem {
    const apiRoute = module.defaultRoute.startsWith('/') ? module.defaultRoute : `/${module.defaultRoute}`;
    const route = apiRoute === '/overview'
      ? '/dashboard'
      : apiRoute === '/balance-sheet-items'
        ? '/balance-sheet-item-name'
        : apiRoute;
    const icon = module.icon.startsWith('pi ') ? module.icon : `pi pi-${module.icon}`;
    return { label: module.name, icon, route };
  }

  private normalizeSidebarNav(items: NavItem[]): NavItem[] {
    const normalized = items
      .filter(item => !this.hiddenSidebarRoutes.has(item.route))
      .map(item => item.route === '/dashboard' ? { ...item, label: 'Overview' } : item);

    const hasRevenue = normalized.some(item => item.route === this.revenueNavItem.route);
    const canViewDashboard = this.auth.session()?.user.isTenantAdmin
      || this.acl.hasPermission('dashboard.view');
    if (hasRevenue || !canViewDashboard) {
      return this.insertRevenueAdjacentNavItems(normalized);
    }

    const overviewIndex = normalized.findIndex(item => item.route === '/dashboard');
    if (overviewIndex < 0) {
      return this.insertRevenueAdjacentNavItems([this.revenueNavItem, ...normalized]);
    }

    return this.insertRevenueAdjacentNavItems([
      ...normalized.slice(0, overviewIndex + 1),
      this.revenueNavItem,
      ...normalized.slice(overviewIndex + 1),
    ]);
  }

  private insertRevenueAdjacentNavItems(items: NavItem[]): NavItem[] {
    const canViewDashboard = this.auth.session()?.user.isTenantAdmin
      || this.acl.hasPermission('dashboard.view');
    const availableAdjacentItems = this.revenueAdjacentNavItems
      .filter(() => canViewDashboard)
      .filter(item => !items.some(existing => existing.route === item.route));

    if (availableAdjacentItems.length === 0) {
      return items;
    }

    const revenueIndex = items.findIndex(item => item.route === this.revenueNavItem.route);
    if (revenueIndex < 0) {
      return [...availableAdjacentItems, ...items];
    }

    return [
      ...items.slice(0, revenueIndex + 1),
      ...availableAdjacentItems,
      ...items.slice(revenueIndex + 1),
    ];
  }

  get userName(): string {
    return this.auth.session()?.user.userName ?? '';
  }

  get userRole(): string {
    const roles = this.auth.session()?.user.roles ?? [];
    return roles.filter((role) => role.trim().toLowerCase() !== 'superadmin').join(', ');
  }

  get userInitials(): string {
    const name = this.userName.trim();
    if (!name) return '';
    return name.split(/\s+/).slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
  }

  /** Count of active slicers — drives the badge number on the Filter button in the topbar. */

}
